import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { preferencesService } from '@/lib/preferencesService';
import { NotificationPreferences, defaultPreferences } from '@/types/notifications';
import { useToast } from '@/hooks/use-toast';

export interface UseNotificationPreferencesReturn {
  preferences: NotificationPreferences;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  updateWhatsAppPreferences: (updates: Partial<NotificationPreferences['whatsapp']>) => Promise<void>;
  updateEmailPreferences: (updates: Partial<NotificationPreferences['email']>) => Promise<void>;
  updateWebPushPreferences: (updates: Partial<NotificationPreferences['webPush']>) => Promise<void>;
  updateDailySummaryPreferences: (updates: Partial<NotificationPreferences['dailySummary']>) => Promise<void>;
  updateAllPreferences: (preferences: NotificationPreferences) => Promise<void>;
  refreshPreferences: () => Promise<void>;
  resetToDefaults: () => Promise<void>;
}

export function useNotificationPreferences(): UseNotificationPreferencesReturn {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [preferences, setPreferences] = useState<NotificationPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load preferences when user changes
  useEffect(() => {
    if (!user) {
      setPreferences(defaultPreferences);
      setIsLoading(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const loadPreferences = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load initial preferences
        const initialPrefs = await preferencesService.loadPreferences(user.uid);
        setPreferences(initialPrefs);

        // Subscribe to real-time updates
        unsubscribe = preferencesService.subscribeToPreferences(user.uid, (updatedPrefs) => {
          setPreferences(updatedPrefs);
        });

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load preferences';
        setError(errorMessage);
        console.error('Error loading preferences:', err);
        
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();

    // Cleanup subscription on unmount or user change
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (user) {
        preferencesService.unsubscribeFromPreferences(user.uid);
      }
    };
  }, [user, toast]);

  const updateWhatsAppPreferences = useCallback(async (updates: Partial<NotificationPreferences['whatsapp']>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setIsSaving(true);
      setError(null);
      
      const updatedPrefs = await preferencesService.updateWhatsAppPreferences(user.uid, updates);
      setPreferences(updatedPrefs);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update WhatsApp preferences';
      setError(errorMessage);
      console.error('Error updating WhatsApp preferences:', err);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [user, toast]);

  const updateEmailPreferences = useCallback(async (updates: Partial<NotificationPreferences['email']>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setIsSaving(true);
      setError(null);
      
      const updatedPrefs = await preferencesService.updateEmailPreferences(user.uid, updates);
      setPreferences(updatedPrefs);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update email preferences';
      setError(errorMessage);
      console.error('Error updating email preferences:', err);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [user, toast]);

  const updateWebPushPreferences = useCallback(async (updates: Partial<NotificationPreferences['webPush']>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setIsSaving(true);
      setError(null);
      
      const updatedPrefs = await preferencesService.updateWebPushPreferences(user.uid, updates);
      setPreferences(updatedPrefs);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update web push preferences';
      setError(errorMessage);
      console.error('Error updating web push preferences:', err);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [user, toast]);

  const updateDailySummaryPreferences = useCallback(async (updates: Partial<NotificationPreferences['dailySummary']>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setIsSaving(true);
      setError(null);

      const updatedPrefs = await preferencesService.updateDailySummaryPreferences(user.uid, updates);
      setPreferences(updatedPrefs);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update daily summary preferences';
      setError(errorMessage);
      console.error('Error updating daily summary preferences:', err);

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [user, toast]);

  const updateAllPreferences = useCallback(async (newPreferences: NotificationPreferences) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setIsSaving(true);
      setError(null);
      
      await preferencesService.savePreferences(user.uid, newPreferences);
      setPreferences(newPreferences);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update preferences';
      setError(errorMessage);
      console.error('Error updating all preferences:', err);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [user, toast]);

  const refreshPreferences = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const refreshedPrefs = await preferencesService.loadPreferences(user.uid);
      setPreferences(refreshedPrefs);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh preferences';
      setError(errorMessage);
      console.error('Error refreshing preferences:', err);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  const resetToDefaults = useCallback(async () => {
    if (!user) return;

    try {
      setIsSaving(true);
      setError(null);
      
      await preferencesService.savePreferences(user.uid, defaultPreferences);
      setPreferences(defaultPreferences);
      
      toast({
        title: "Success",
        description: "Preferences reset to defaults",
      });
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset preferences';
      setError(errorMessage);
      console.error('Error resetting preferences:', err);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [user, toast]);

  return {
    preferences,
    isLoading,
    isSaving,
    error,
    updateWhatsAppPreferences,
    updateEmailPreferences,
    updateWebPushPreferences,
    updateDailySummaryPreferences,
    updateAllPreferences,
    refreshPreferences,
    resetToDefaults,
  };
}